import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material'
import AppRegistrationOutlinedIcon from '@mui/icons-material/AppRegistrationOutlined'
import { useState } from 'react'
import adminFieldService from '../../../services/admin/field'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { toast } from 'react-toastify'
import { FIELD_TYPES } from '../../../utility/constants'

const GridLayoutView = ({ fields = [], onClose, allFields = [], updateFields }) => {
  const { user } = useAuthContext()

  const [colSpanData, setColSpanData] = useState(
    fields.map((it) => {
      return {
        id: it.id,
        field_template_name: it.field_template_name,
        colspan_ui: it.colspan_ui,
        is_start_on_new_line_ui: it.is_start_on_new_line_ui,
      }
    }) || []
  )

  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [currentSelectedIndex, setCurrentSelectedIndex] = useState(null)
  const [colSpanValue, setColSpanValue] = useState(null)
  const [isStartOnNewLine, setIsStartOnNewLine] = useState(null)

  const handleUpdate = () => {
    setColSpanData(
      colSpanData.map((it, i) => {
        if (currentSelectedIndex == i) {
          it.colspan_ui = colSpanValue
          it.is_start_on_new_line_ui = isStartOnNewLine
        }
        return it
      })
    )
    handleCloseEditDialog()
  }

  const handleOpenEditDialog = (index) => {
    setColSpanValue(colSpanData[index]?.colspan_ui)
    setIsStartOnNewLine(colSpanData[index]?.is_start_on_new_line_ui)
    setEditDialogOpen(true)
    setCurrentSelectedIndex(index)
  }

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false)
    setColSpanValue(null)
    setIsStartOnNewLine(null)
    setCurrentSelectedIndex(null)
  }

  const handleSaveChanges = async () => {
    try {
      const updatedAllFields = allFields.map((field) => {
        let gridData = colSpanData.find((it) => it.id == field.id)

        if (gridData) {
          field.colspan_ui = gridData.colspan_ui
          field.is_start_on_new_line_ui = gridData.is_start_on_new_line_ui
        }
        return field
      })

      updateFields(updatedAllFields)
      await adminFieldService.updateGridLayout(user, colSpanData)
      toast.success('Grid Layout Updated Succefully.')
      onClose()
    } catch (error) {
      console.error(error)
      toast.error('Something Went Wrong')
    }
  }

  const TOTAL_COLUMN_SPAN = 4
  let remainingSpan = TOTAL_COLUMN_SPAN // Tracks available space in the current row
  return (
    <>
      <Dialog fullWidth maxWidth={'xl'} open={true} onClose={onClose}>
        <DialogTitle>Grid Layout View</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(4, 1fr)`,
              gap: '16px',
              marginBlock: '20px',
            }}
          >
            {fields.map((field, index) => {
              if (
                colSpanData[index].is_start_on_new_line_ui &&
                remainingSpan > 0 &&
                remainingSpan < TOTAL_COLUMN_SPAN
              ) {
                const spacer = (
                  <div
                    key={`spacer-${field.id}`}
                    style={{
                      gridColumn: `span ${remainingSpan}`,
                    }}
                  ></div>
                )

                // Decrease remainingSpan for the current field
                const currentFieldSpan = colSpanData[index].colspan_ui || 1
                remainingSpan = TOTAL_COLUMN_SPAN - currentFieldSpan

                // If the row is full, reset remainingSpan
                if (remainingSpan <= 0) {
                  remainingSpan = TOTAL_COLUMN_SPAN
                }

                return (
                  <>
                    {spacer}
                    <Box
                      sx={{
                        gridColumn: `span ${colSpanData[index].colspan_ui}`,
                        border: '1px solid grey',
                        padding: '5px 10px 5px 10px',
                        borderRadius: '5px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Typography variant="subtitle1" fontSize={'12px'}>
                        {field.field_template_name}
                        {' - '}({field.data_structure})
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => {
                          handleOpenEditDialog(index)
                        }}
                      >
                        <AppRegistrationOutlinedIcon />
                      </IconButton>
                    </Box>
                  </>
                )
              }

              // Decrease remainingSpan for the current field
              const currentFieldSpan = colSpanData[index].colspan_ui || 1
              remainingSpan -= currentFieldSpan

              // If the row is full, reset remainingSpan
              if (remainingSpan <= 0) {
                remainingSpan = TOTAL_COLUMN_SPAN
              }

              return (
                <Box
                  sx={{
                    gridColumn: `span ${colSpanData[index].colspan_ui}`,
                    border: '1px solid grey',
                    padding: '5px 10px 5px 10px',
                    borderRadius: '5px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography variant="subtitle1" fontSize={'12px'}>
                    {field.field_template_name}
                    {' - '}({field.data_structure})
                  </Typography>
                  {[FIELD_TYPES.TABLE, FIELD_TYPES.CUSTOM_VIEW].includes(
                    field.data_structure
                  ) ? null : (
                    <IconButton
                      size="small"
                      onClick={() => {
                        handleOpenEditDialog(index)
                      }}
                    >
                      <AppRegistrationOutlinedIcon />
                    </IconButton>
                  )}
                </Box>
              )
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSaveChanges}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      {editDialogOpen && currentSelectedIndex + 1 && (
        <Dialog fullWidth maxWidth={'sm'} open={editDialogOpen} onClose={handleCloseEditDialog}>
          <DialogTitle>
            Edit Grid Item ({colSpanData[currentSelectedIndex]?.field_template_name || ''})
          </DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <FormControl sx={{ m: 1, minWidth: 120 }}>
                <InputLabel color="primary" id="colpanlabel">
                  Col Span
                </InputLabel>
                <Select
                  labelId="colpanlabel"
                  id="colspanid"
                  label="Col Span"
                  color="primary"
                  value={colSpanValue}
                  onChange={(e) => setColSpanValue(e.target.value)}
                >
                  <MenuItem value={1}>1/4</MenuItem>
                  <MenuItem value={2}>2/4</MenuItem>
                  <MenuItem value={3}>3/4</MenuItem>
                  <MenuItem value={4}>4/4</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ m: 1, minWidth: 120 }}>
                <InputLabel color="primary" id="isStartOnNewLineLabel">
                  Is Start on new line
                </InputLabel>
                <Select
                  labelId="isStartOnNewLineLabel"
                  id="isStartOnNewLineId"
                  label="Is Start on new line"
                  color="primary"
                  value={isStartOnNewLine}
                  onChange={(e) => setIsStartOnNewLine(e.target.value)}
                >
                  <MenuItem value={true}>Yes</MenuItem>
                  <MenuItem value={false}>No</MenuItem>
                </Select>
              </FormControl>
            </div>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="primary" onClick={handleCloseEditDialog}>
              Cancel
            </Button>
            <Button variant="contained" color="primary" onClick={handleUpdate}>
              Update
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  )
}

export default GridLayoutView
